import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle } from '@phosphor-icons/react';
import { AssetInstancesTable } from 'components/assets';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MaintenanceCalendar } from 'shared/components/maintenance/sections/MaintenanceCalendar';
import { useAllMaintenanceSchedules, useMaintenanceEvents } from 'hooks/useAssets';
import { Spinner } from 'components/ui/spinner';
import AssetCategoriesContent from './AssetCategoriesContent';
import AssetStatisticsTab from './statistics/AssetStatisticsTab';
import MissingAssetsTab from './MissingAssetsTab';
import VoorzieningenMapper from './mapper/VoorzieningenMapper';

/**
 * AssetsScreen - Voorzieningen with tabbed navigation
 *
 * Three tabs: Overzicht (asset table), Onderhoudskalender, Categorieën & Types.
 */
const AssetsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { schedules, isLoading: isLoadingSchedules, refetch: refetchSchedules } = useAllMaintenanceSchedules();
  const { events, isLoading: isLoadingEvents, refetch: refetchEvents } = useMaintenanceEvents();

  const refetchMaintenance = () => {
    refetchSchedules();
    refetchEvents();
  };

  return (
    <div className="mx-auto px-4 py-6 max-w-[1600px]">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Voorzieningen</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Beheer alle voorzieningen in uw vastgoedportefeuille
            </p>
          </div>
          <Button onClick={() => navigate('/assets/bulk-create')}>
            <PlusCircle className="h-4 w-4 mr-1" />
            Nieuwe Voorziening
          </Button>
        </div>
      </div>

      {/* Tabbed content */}
      <Tabs defaultValue="overzicht">
        <TabsList className="h-auto bg-transparent p-0 rounded-none border-b border-border w-full justify-start gap-0">
          <TabsTrigger value="overzicht" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground hover:text-foreground px-4 py-2.5 font-medium">Overzicht</TabsTrigger>
          <TabsTrigger value="onderhoud" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground hover:text-foreground px-4 py-2.5 font-medium">Onderhoudskalender</TabsTrigger>
          <TabsTrigger value="categorieen" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground hover:text-foreground px-4 py-2.5 font-medium">Categorieën & Types</TabsTrigger>
          <TabsTrigger value="ontbrekend" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground hover:text-foreground px-4 py-2.5 font-medium">Ontbrekende voorzieningen</TabsTrigger>
          <TabsTrigger value="mapper" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground hover:text-foreground px-4 py-2.5 font-medium">Hierarchische weergave</TabsTrigger>
          <TabsTrigger value="statistieken" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground hover:text-foreground px-4 py-2.5 font-medium">Statistieken</TabsTrigger>
        </TabsList>

        <TabsContent value="overzicht" className="mt-6">
          <AssetInstancesTable />
        </TabsContent>

        <TabsContent value="onderhoud" className="mt-6">
          {isLoadingSchedules || isLoadingEvents ? (
            <div className="flex items-center justify-center py-8">
              <Spinner className="h-5 w-5" />
              <span className="ml-2 text-sm text-muted-foreground">
                Onderhoud laden...
              </span>
            </div>
          ) : (
            <MaintenanceCalendar
              schedules={schedules}
              events={events}
              onRefetch={refetchMaintenance}
            />
          )}
        </TabsContent>

        <TabsContent value="categorieen" className="mt-6">
          <AssetCategoriesContent />
        </TabsContent>

        <TabsContent value="ontbrekend" className="mt-6">
          <MissingAssetsTab />
        </TabsContent>

        <TabsContent value="mapper" className="mt-6">
          <VoorzieningenMapper />
        </TabsContent>

        <TabsContent value="statistieken" className="mt-6">
          <AssetStatisticsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AssetsScreen;
